/**
 * ReferenceAgentSettingsCard — the reference agent's card on Settings → AI agents (BR-28,
 * BR-SETTINGS-AGENTS). One on/off switch only: the reference agent has no review switch (its
 * referee emails are never held) and no playbook, so unlike AgentSettingsCard there is no
 * playbook block. Same card shell as AgentSettingsCard.
 *
 * It is the target of the "Turn it on in Settings → AI agents" links (DC-17): when the page is
 * opened at `#reference-agent` the card scrolls into view and takes focus, so one tap from the
 * lead's Verifications tab lands on the switch, not at the top of a long page. The cards above it
 * change height as they load, so the card keeps itself in view until the landlord scrolls.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { REFERENCE_AGENT_CARD_ID } from '../../lib/reference-check-links';
import { AgentDefaultPolicySwitches } from './AgentDefaultPolicySwitches';

/** How long a deep-linked card keeps correcting its position while the page above it loads. */
const SETTLE_MS = 3000;

export function ReferenceAgentSettingsCard() {
  const headingId = 'agent-settings-card-title-reference_coordinator';
  const { hash } = useLocation();
  const cardRef = useRef<HTMLElement>(null);
  const targeted = hash === `#${REFERENCE_AGENT_CARD_ID}`;

  useEffect(() => {
    if (!targeted) return;
    const card = cardRef.current;
    if (!card) return;
    const scrollToCard = () => card.scrollIntoView?.({ block: 'start' });
    scrollToCard();
    card.focus({ preventScroll: true });

    // Re-scroll as the cards above fill in, until the time is up or the landlord takes over.
    if (typeof ResizeObserver === 'undefined' || !card.parentElement) return;
    const observer = new ResizeObserver(scrollToCard);
    observer.observe(card.parentElement);
    const stop = () => observer.disconnect();
    const timer = window.setTimeout(stop, SETTLE_MS);
    const takeOver = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    takeOver.forEach((type) => window.addEventListener(type, stop, { once: true, passive: true }));
    return () => {
      stop();
      window.clearTimeout(timer);
      takeOver.forEach((type) => window.removeEventListener(type, stop));
    };
  }, [targeted]);

  return (
    <section
      ref={cardRef}
      id={REFERENCE_AGENT_CARD_ID}
      tabIndex={-1}
      data-testid="agent-settings-card-reference_coordinator"
      aria-labelledby={headingId}
      className="scroll-mt-4 rounded-lg border border-border bg-card p-[var(--card-padding)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <h2 id={headingId} className="mb-4 text-base font-semibold text-foreground">
        Reference agent
      </h2>

      <AgentDefaultPolicySwitches agentKind="reference_coordinator" />
    </section>
  );
}

export default ReferenceAgentSettingsCard;
